import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { RentalService } from '../../../core/api/rental.service';
import { BookingService } from '../../../core/api/booking.service';
import { CarService } from '../../../core/api/car.service';
import { ExtraServiceService } from '../../../core/api/extra-service.service';
import { Rental, RentalCreate } from '../../../core/models/rental.model';
import { ExtraService } from '../../../core/models/extra-service.model';
import { Image } from '../../../core/models/image.model';
import { CarAvailability } from '../../../core/models/car.model';
import { PaymentMethod } from '../../../core/models/payment.model';
import { parseApiDateTime, toApiDateTime } from '../../../shared/utils/date.utils';
import { DatetimeInputComponent } from '../../../shared/components/datetime-input/datetime-input.component';
import { ImageUploadComponent } from '../../../shared/components/image-upload/image-upload.component';
import { CarSelectComponent } from '../../../shared/components/car-select/car-select.component';
import { ClientSelectComponent } from '../../../shared/components/client-select/client-select.component';

@Component({
  selector: 'app-rental-create',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, MatCardModule, MatIconModule, MatButtonModule,
    MatFormFieldModule, MatInputModule, MatSelectModule, MatOptionModule, MatCheckboxModule,
    MatSnackBarModule, MatProgressSpinnerModule,
    MatDividerModule, RouterLink,
    DatetimeInputComponent, ImageUploadComponent, CarSelectComponent,
    ClientSelectComponent,
  ],
  templateUrl: './rental-create.component.html',
  styleUrl: './rental-create.component.scss',
})
export class RentalCreateComponent implements OnInit {
  form: FormGroup;
  submitting = false;
  availableCars: CarAvailability[] = [];
  bookingInfo: { id: number; client: number; car: number; clientName: string; carTitle: string } | null = null;
  extraServices: ExtraService[] = [];
  PaymentMethod = PaymentMethod;
  paymentMethodOptions = [
    { value: PaymentMethod.CASH, label: 'Наличные' },
    { value: PaymentMethod.CARD, label: 'Карта' },
    { value: PaymentMethod.TRANSFER, label: 'Перевод' },
    { value: PaymentMethod.OTHER, label: 'Другое' },
  ];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private rentalService: RentalService,
    private bookingService: BookingService,
    private carService: CarService,
    private extraServiceService: ExtraServiceService,
    private snackBar: MatSnackBar,
  ) {
    this.form = this.fb.group({
      client: [null as number | null, Validators.required],
      car: [null, Validators.required],
      handoverLocation: ['', Validators.required],
      handoverDate: [new Date() as Date | null, Validators.required],
      expectedReturnDate: [null as Date | null],
      mileageBefore: [0, [Validators.required, Validators.min(0)]],
      fuelLevelBefore: [null],
      notes: [''],
      route: [''],
      paymentAmount: [null as number | null],
      paymentMethod: [PaymentMethod.CASH],
      imagesBefore: [[] as Image[]],
      extraServices: this.fb.array([] as FormGroup[]),
    });
  }

  ngOnInit(): void {
    this.loadCars();
    this.loadExtraServices();
    const bookingId = this.route.snapshot.queryParamMap.get('booking');
    if (bookingId) {
      this.loadBookingData(Number(bookingId));
    }
  }

  get extraServicesForm(): FormArray<FormGroup> {
    return this.form.get('extraServices') as FormArray<FormGroup>;
  }

  /**
   * Сбор `extra_services` из FormArray. Возвращает только выбранные
   * услуги с положительной суммой. Если ничего не выбрано — вернётся
   * пустой массив (так и отправляем на бэк: replace-режим).
   */
  private collectExtraServices(): { service: number; amount: number }[] {
    return this.extraServicesForm.controls
      .map((group) => group.getRawValue() as { service: number; selected: boolean; amount: number | null })
      .filter((row) => row.selected && row.amount != null && Number(row.amount) > 0)
      .map((row) => ({ service: row.service, amount: Number(row.amount) }));
  }

  onSubmit(): void {
    if (this.form.invalid || this.submitting) return;
    this.submitting = true;
    const v = this.form.value;
    const handoverDate = v.handoverDate ? parseApiDateTime(v.handoverDate) : null;
    if (!handoverDate) {
      this.form.markAllAsTouched();
      this.submitting = false;
      return;
    }
    const expectedReturnDate = v.expectedReturnDate ? parseApiDateTime(v.expectedReturnDate) : null;
    const data: RentalCreate = {
      client: v.client,
      car: v.car,
      booking: this.bookingInfo?.id || null,
      handover_location: v.handoverLocation,
      handover_datetime: toApiDateTime(handoverDate),
      mileage_before: v.mileageBefore,
      fuel_level_before: v.fuelLevelBefore ?? null,
      expected_return_datetime: expectedReturnDate ? toApiDateTime(expectedReturnDate) : null,
      notes: v.notes || '',
      route: v.route || '',
      payment_amount: v.paymentAmount ?? null,
      payment_method: v.paymentAmount != null && v.paymentAmount !== '' ? v.paymentMethod ?? null : null,
      images_before: (v.imagesBefore || []).map((img: Image) => img.id),
      extra_services: this.collectExtraServices(),
    };
    this.rentalService.create(data).subscribe({
      next: (r: Rental) => {
        this.router.navigate(['/rentals', r.id]);
        this.snackBar.open('Прокат создан', 'ОК', { duration: 3000 });
      },
      error: (err: any) => {
        this.submitting = false;
        const errorData = err.error;
        if (errorData && typeof errorData === 'object' && !Array.isArray(errorData)) {
          this.setServerErrors(errorData);
          // Ошибки по вложенным полям (extra_services) бэк может прислать
          // без маппинга на конкретный контрол — показываем в snackBar.
          const nonField = errorData.all || errorData.non_field_errors || errorData.extra_services;
          if (nonField) {
            const msg = Array.isArray(nonField) ? nonField[0] : String(nonField);
            this.snackBar.open(msg, 'ОК', { duration: 5000 });
          }
        } else {
          const msg = Array.isArray(errorData) ? errorData[0] : 'Ошибка создания';
          this.snackBar.open(msg, 'ОК', { duration: 5000 });
        }
      },
    });
  }

  private loadCars(): void {
    const today = new Date().toISOString().split('T')[0];
    this.carService.getAvailability(today).subscribe(cars => {
      this.availableCars = cars;
    });
  }

  private loadExtraServices(): void {
    this.extraServiceService.list(true).subscribe({
      next: (services) => {
        this.extraServices = services;
        this.buildExtraServicesForm(services);
      },
      error: () => {
        // Если список не загрузился — форма остаётся с пустым массивом,
        // пользователь просто не увидит чекбоксы. Не блокируем создание.
        this.extraServices = [];
      },
    });
  }

  /**
   * Строит FormArray под список активных услуг. На каждую услугу —
   * FormGroup: `service` (disabled, держит id), `selected` (bool),
   * `amount` (number, валидация на > 0 включается только при selected).
   * Disabled-контрол всё равно попадает в `getRawValue()`, что нам и нужно.
   */
  private buildExtraServicesForm(services: ExtraService[]): void {
    // Очищаем, если уже что-то было (теоретически возможно при повторном вызове).
    while (this.extraServicesForm.length > 0) {
      this.extraServicesForm.removeAt(0);
    }
    for (const es of services) {
      const group = this.fb.group({
        service: [{ value: es.id, disabled: true }, Validators.required],
        selected: [false],
        amount: this.fb.control<number | null>(null, [Validators.min(0.01)]),
      });
      this.extraServicesForm.push(group);
    }
  }

  private loadBookingData(bookingId: number): void {
    this.bookingService.get(bookingId).subscribe({
      next: (b: any) => {
        this.bookingInfo = {
          id: b.id,
          client: b.client,
          car: b.car,
          clientName: b.client_name,
          carTitle: b.car_title,
        };
        this.form.patchValue({
          client: b.client,
          car: b.car,
        });
      },
    });
  }

  private readonly apiFieldToControl: Record<string, string> = {
    client: 'client',
    car: 'car',
    handover_location: 'handoverLocation',
    handover_datetime: 'handoverDate',
    mileage_before: 'mileageBefore',
    fuel_level_before: 'fuelLevelBefore',
    expected_return_datetime: 'expectedReturnDate',
    notes: 'notes',
    route: 'route',
    payment_amount: 'paymentAmount',
    payment_method: 'paymentMethod',
    images_before: 'imagesBefore',
    extra_services: 'extraServices',
  };

  private setServerErrors(errors: Record<string, any>): void {
    for (const [apiField, controlName] of Object.entries(this.apiFieldToControl)) {
      const control = this.form.get(controlName);
      if (control && errors.hasOwnProperty(apiField)) {
        const messages = errors[apiField];
        const msg = Array.isArray(messages) ? messages[0] : String(messages);
        control.setErrors({ ...control.errors, serverError: msg });
      }
    }
  }
}
